import { useState } from "react";
import { Check, Copy, Instagram } from "lucide-react";
import { SITE } from "@/config/site";
import { formatPrice } from "@/lib/format";
import { shareUrl } from "@/lib/shareCart";
import { patternBg } from "@/lib/patternBg";

// Para quem prefere acabar a encomenda com a Alexandra: a mensagem leva a lista das peças e um link
// que ela abre no painel para registar a encomenda tal como foi escolhida.
const BG = patternBg("vichy-rosa-claro", "246,226,226", 0.72, 150);
const pill = "inline-flex min-h-[44px] items-center gap-2 rounded-full bg-ivory px-5 text-sm font-medium text-ink ring-1 ring-bordeaux/15 transition-colors hover:text-bordeaux";

export const ShareCart = ({ items, subtotal, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const message = () => [
    "Olá Alexandra! Escolhi estas peças no site e gostava de continuar a encomenda contigo 😊",
    ...items.map((i) => `• ${i.qty}x ${i.product.name}${i.pattern ? ` (${i.pattern.name})` : ""}`),
    `Subtotal: ${formatPrice(subtotal)}`,
    "",
    `Link do pedido: ${shareUrl(items)}`,
  ].join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message());
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      window.prompt("Copia esta mensagem e envia-a à Alexandra:", message());
    }
  };
  // Versão discreta (gaveta do carrinho): só uma ligação sublinhada.
  if (compact) {
    return (
      <div className="space-y-1" data-testid="cart-share-compact">
        <p className="text-center text-xs text-taupe-strong">Queres finalizar a encomenda diretamente com a Alexandra?</p>
        <div className="flex flex-wrap justify-center gap-2">
          <button type="button" onClick={copy} data-testid="cart-share-compact-copy" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-xs text-ink ring-1 ring-bordeaux/20 hover:text-bordeaux">
            {copied ? <Check className="h-3.5 w-3.5 text-bordeaux" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5 text-bordeaux" aria-hidden="true" />} {copied ? "Copiada!" : "Copiar mensagem"}
          </button>
          {/* O Instagram não aceita texto já escrito: copia a mensagem e abre o perfil, é só colar na conversa. */}
          <a href={SITE.instagram.url} target="_blank" rel="noopener noreferrer" onClick={copy} data-testid="cart-share-compact-instagram" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-xs text-ink ring-1 ring-bordeaux/20 hover:text-bordeaux">
            <Instagram className="h-3.5 w-3.5 text-bordeaux" aria-hidden="true" /> Instagram
          </a>
        </div>
        {copied && <p className="text-center text-[11px] text-taupe" role="status">Mensagem copiada: cola-a na conversa com {SITE.instagram.handle}.</p>}
      </div>
    );
  }
  return (
    <div className="relative isolate overflow-hidden rounded-3xl bg-[#F3DCDC] p-5 ring-1 ring-bordeaux/10" data-testid="cart-share">
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" style={BG} />
      <p className="font-display text-[1.0625rem] leading-snug text-ink">Queres finalizar a encomenda diretamente com a Alexandra? <span aria-hidden="true">🤍</span></p>
      <p className="mt-1.5 max-w-[46ch] text-[13.5px] leading-relaxed text-ink/75">Copia a mensagem e envia-a por Instagram. A Alexandra vê exatamente o que escolheste e continua a partir daí.</p>
      <div className="mt-3.5 flex flex-wrap gap-2.5">
        <button type="button" onClick={copy} data-testid="cart-share-copy" className={pill}>
          {copied ? <Check className="h-4 w-4 text-bordeaux" aria-hidden="true" /> : <Copy className="h-4 w-4 text-bordeaux" aria-hidden="true" />} {copied ? "Mensagem copiada!" : "Copiar mensagem"}
        </button>
        <a href={SITE.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="cart-share-instagram" className={pill}><Instagram className="h-4 w-4 text-bordeaux" aria-hidden="true" /> {SITE.instagram.handle}</a>
      </div>
      {copied && <p className="mt-2 text-xs text-taupe-strong" role="status">Agora cola-a numa conversa com {SITE.instagram.handle}.</p>}
    </div>
  );
};
